import { useState } from 'react';
import { Popover } from '@allrouted/ui/Popover';

/**
 * The sections, across the top.
 *
 * Visible as a menu, because a destination you can see is one you do not have
 * to go looking for. Both shapes are rendered and CSS chooses: below about a
 * laptop width the header runs out of room beside the territory, the date and
 * the day's actions, and the row folds into a dropdown naming wherever you are.
 *
 * Done in CSS rather than by measuring in JavaScript -- a resize listener that
 * decides which nav to show is a layout that flickers on load and disagrees
 * with itself during a drag.
 *
 * Setup is deliberately not one of these. It configures one territory rather
 * than the application, so it hangs off the territory picker instead -- see
 * the cog in the header.
 */

/*
 * Billing and admin are sections with URLs but no place in the menu. A
 * dispatcher opens Orders forty times a day and Billing twice a year, and
 * giving them equal weight in the same row would be a poor trade for the row.
 * They hang off the account menu instead.
 */
export type Section =
  | 'home'
  | 'orders'
  | 'tracking'
  | 'simulations'
  | 'fleet'
  | 'billing'
  | 'account'
  | 'audit'
;

interface Item {
  id: Section;
  label: string;
  icon: JSX.Element;
}

const ITEMS: Item[] = [
  {
    id: 'home',
    label: 'Home',
    icon: (
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M2 5.5L8 2.5l6 3v7l-6 3-6-3v-7z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path d="M8 5.5v10M2 5.5l6 3 6-3" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: (
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="2.5" y="2" width="11" height="12" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
        <path
          d="M5.5 6h5M5.5 8.8h5M5.5 11.5h3"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: 'tracking',
    label: 'Tracking',
    icon: (
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M8 14.5s5-4.2 5-7.6A5 5 0 003 6.9c0 3.4 5 7.6 5 7.6z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <circle cx="8" cy="6.8" r="1.8" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    id: 'simulations',
    label: 'Simulations',
    icon: (
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M6.5 1.8v3.6L2.8 12a1.5 1.5 0 001.3 2.2h7.8A1.5 1.5 0 0013.2 12L9.5 5.4V1.8"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path d="M5.6 1.8h4.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
];

export function TopNav({
  section,
  onSelect,
}: {
  section: Section;
  onSelect: (section: Section) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = ITEMS.find((i) => i.id === section) ?? ITEMS[0]!;

  return (
    <>
      <div className="brand">
        <img className="brand-wordmark" src="/brand/allrouted-wordmark.png" alt="AllRouted" width="466" height="72" />
      </div>

      <nav className="topnav" aria-label="Sections">
        {ITEMS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="topnav-item"
            aria-current={section === item.id ? 'page' : undefined}
            onClick={() => onSelect(item.id)}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      <div className="pop-anchor section-anchor">
        <button
          type="button"
          className="section-picker"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {current.icon}
          <span>{current.label}</span>
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path
              d="M2 4l3 3 3-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <Popover open={open} onClose={() => setOpen(false)}>
          <div role="listbox">
            {ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={item.id === section}
                className="pop-item"
                onClick={() => {
                  onSelect(item.id);
                  setOpen(false);
                }}
              >
                <span className="pop-item-main">
                  {item.icon}
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </Popover>
      </div>
    </>
  );
}

/**
 * The cog beside the territory picker.
 *
 * Setup belongs to whichever territory is selected, so it sits with the control
 * that selects one rather than competing with Dispatch and Orders for the same
 * row. Same destination as "Territory settings" inside the picker itself.
 */
export function SettingsButton({
  active,
  onClick,
}: {
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="icon-btn"
      aria-current={active ? 'page' : undefined}
      aria-label="Territory settings"
      title="Territory settings"
      onClick={onClick}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.4" />
        <path
          d="M8 1.6l.5 1.6a5 5 0 011.4.6l1.5-.8 1.6 1.6-.8 1.5a5 5 0 01.6 1.4l1.6.5v2.2l-1.6.5a5 5 0 01-.6 1.4l.8 1.5-1.6 1.6-1.5-.8a5 5 0 01-1.4.6L8 14.4l-.5-1.6a5 5 0 01-1.4-.6l-1.5.8-1.6-1.6.8-1.5a5 5 0 01-.6-1.4L1.6 8.1V6l1.6-.5a5 5 0 01.6-1.4l-.8-1.5L4.6 1l1.5.8a5 5 0 011.4-.6L8 1.6z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/**
 * Who is signed in.
 *
 * Initials rather than a full address in the header: on a 1280px laptop the row
 * already carries five sections, a territory, a date and the day's actions, and
 * an email is the widest thing that would be competing for it. The address
 * itself is one click away, where somebody looking for it will look.
 */
export function AccountButton({
  email,
  accountName,
  role,
  onGo,
  onSignOut,
}: {
  email: string;
  accountName: string;
  role: string;
  onGo: (section: Section) => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="pop-anchor">
      <button
        type="button"
        className="avatar-btn"
        aria-label={`Signed in as ${email}`}
        title={email}
        onClick={() => setOpen((v) => !v)}
      >
        {initials(email)}
      </button>
      <Popover open={open} onClose={() => setOpen(false)} align="right">
        <div className="pop-head">
          <strong>{email}</strong>
          <span>
            {accountName} &middot; {role}
          </span>
        </div>
        <button
          type="button"
          className="pop-item"
          onClick={() => {
            setOpen(false);
            onGo('account');
          }}
        >
          Users and API keys
        </button>
        <button
          type="button"
          className="pop-item"
          onClick={() => {
            setOpen(false);
            onGo('billing');
          }}
        >
          Plan and billing
        </button>
        {/*
          * Opens in a new tab, and is a real link rather than a button: a
          * developer reading the reference wants it beside the console, not
          * instead of it, and wants middle-click and "copy link" to work.
          */}
        <button
          type="button"
          className="pop-item"
          onClick={() => {
            setOpen(false);
            onGo('audit');
          }}
        >
          API activity
        </button>
        <a className="pop-item" href="/docs" target="_blank" rel="noreferrer">
          <span>API reference</span>
          <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
            <path
              d="M4.5 1.5H10.5V7.5M10.5 1.5L5 7M8 10.5H1.5V4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
        </a>
        <button
          type="button"
          className="pop-item"
          onClick={() => {
            setOpen(false);
            onSignOut();
          }}
        >
          Sign out
        </button>
      </Popover>
    </div>
  );
}

function initials(email: string): string {
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}
